import React, { useState, useEffect } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { X, Copy, Check, Mail } from 'lucide-react';
import { useEmailModalStore } from '@/hooks/useEmailCopyModal';
import { toast } from 'react-hot-toast'; 

// CSS for the highlight animation
const highlightAnimation = `
@keyframes highlightFade {
  0% { background-color: rgba(238, 153, 234, 0.2); }
  100% { background-color: transparent; }
}

.highlighted-text {
  animation: highlightFade 1.5s ease-out forwards;
  border-radius: 4px;
  padding: 2px 0;
}`;

interface ToggleOption {
  id: string;
  label: string;
  content: string;
  defaultEnabled: boolean;
  position: 'after_how_it_works' | 'after_first_paragraph' | 'after_why_you' | 'before_thank_you' | 'end';
}

export default function EmailCopyModal() {
  const { isOpen, evaluator, userEmail, closeModal } = useEmailModalStore();
  const [copied, setCopied] = useState(false);
  const [highlightedId, setHighlightedId] = useState<string | null>(null);
  const [toggles, setToggles] = useState<Record<string, boolean>>({
    anonymity: false,
    deadline: false,
    whyYou: true,
    moreContext: false,
    howLong: true,
    appreciation: true,
    result: false
  });

  // Reset toggles when modal opens
  useEffect(() => {
    if (isOpen) {
      setToggles({
        anonymity: false,
        deadline: false,
        whyYou: true,
        moreContext: false,
        howLong: true,
        appreciation: true,
        result: false
      });
      setCopied(false);
    }
  }, [isOpen]);

  const toggleOptions: ToggleOption[] = [
    {
      id: 'anonymity',
      label: 'Anonymity and Independence',
      content: 'All responses are anonymous, so please feel free to be candid.\nThis is a personal development tool and is not connected to any formal HR evaluation or promotion process.',
      defaultEnabled: false,
      position: 'after_how_it_works'
    },
    {
      id: 'deadline',
      label: 'Deadline',
      content: "I'm aiming to gather all feedback by end of next week.\nIf that timeline is difficult for any reason, just let me know.",
      defaultEnabled: false,
      position: 'after_how_it_works'
    },
    {
      id: 'whyYou',
      label: 'Why You',
      content: "Your input is especially valuable to me, I really value the perspective you've had on my work, and I believe it will help me grow meaningfully.",
      defaultEnabled: true,
      position: 'after_first_paragraph'
    },
    {
      id: 'moreContext',
      label: 'More Context',
      content: "This feedback is part of a broader development initiative I'm pursuing, designed to help me reflect and grow beyond what our standard review processes usually offer.",
      defaultEnabled: false,
      position: 'after_why_you'
    },
    {
      id: 'howLong',
      label: 'How Long It Takes',
      content: "The evaluation should take about 30 to 45 minutes. It's mostly structured, with a few open-ended questions toward the end.",
      defaultEnabled: true,
      position: 'after_how_it_works'
    },
    {
      id: 'appreciation',
      label: 'Appreciation of Time and Effort',
      content: "I know your time is valuable, and I'm genuinely grateful that you're taking a moment to support my growth.",
      defaultEnabled: true,
      position: 'before_thank_you'
    },
    {
      id: 'result',
      label: 'The Result',
      content: "Once the process is complete, I'll receive a report with the results.\nEverything will be aggregated and anonymous, allowing me to focus on the themes and patterns across feedback.",
      defaultEnabled: false,
      position: 'end'
    }
  ];

  const handleToggle = (id: string) => {
    setToggles(prev => ({
      ...prev,
      [id]: !prev[id]
    }));
    
    // Only set highlight when toggling ON
    if (!toggles[id]) {
      setHighlightedId(id);
      // Clear the highlight after animation completes
      setTimeout(() => {
        setHighlightedId(null);
      }, 1500);
    }
  };

  const generateEmailContent = () => {
    if (!evaluator) return '';

    // Core message with evaluator's first name
    let content = `Hi ${evaluator.firstName},

As part of my professional development process, I'm gathering feedback to help me gain a more complete view of my performance, and I would love to include you in this process.`;

    // Add "Why You" section if enabled
    if (toggles.whyYou) {
      const isHighlighted = highlightedId === 'whyYou';
      content += `

${isHighlighted ? '<span class="highlighted-text">' : ''}Your input is especially valuable to me, I really value the perspective you've had on my work, and I believe it will help me grow meaningfully.${isHighlighted ? '</span>' : ''}`;
    }

    // Add "More Context" section if enabled and Why You is enabled
    if (toggles.moreContext && toggles.whyYou) {
      const isHighlighted = highlightedId === 'moreContext';
      content += `

${isHighlighted ? '<span class="highlighted-text">' : ''}This feedback is part of a broader development initiative I'm pursuing, designed to help me reflect and grow beyond what our standard review processes usually offer.${isHighlighted ? '</span>' : ''}`;
    } else if (toggles.moreContext) {
      // Add "More Context" after first paragraph if Why You is not enabled
      const isHighlighted = highlightedId === 'moreContext';
      content += `

${isHighlighted ? '<span class="highlighted-text">' : ''}This feedback is part of a broader development initiative I'm pursuing, designed to help me reflect and grow beyond what our standard review processes usually offer.${isHighlighted ? '</span>' : ''}`;
    }

    // Add "How it works" section
    content += `

How it works:
You should receive an email from insightalks.com with your access link and code. If it's not in your inbox, please check your spam folder (and whitelist the address to make sure the access code comes through).
The email includes simple instructions on how to access and complete the evaluation.`;

    // Add sections that come after "How it works"
    if (toggles.anonymity) {
      const isHighlighted = highlightedId === 'anonymity';
      content += `

${isHighlighted ? '<span class="highlighted-text">' : ''}All responses are anonymous, so please feel free to be candid.
This is a personal development tool and is not connected to any formal HR evaluation or promotion process.${isHighlighted ? '</span>' : ''}`;
    }

    if (toggles.deadline) {
      const isHighlighted = highlightedId === 'deadline';
      content += `

${isHighlighted ? '<span class="highlighted-text">' : ''}I'm aiming to gather all feedback by end of next week.
If that timeline is difficult for any reason, just let me know.${isHighlighted ? '</span>' : ''}`;
    }

    if (toggles.howLong) {
      const isHighlighted = highlightedId === 'howLong';
      content += `

${isHighlighted ? '<span class="highlighted-text">' : ''}The evaluation should take about 30 to 45 minutes. It's mostly structured, with a few open-ended questions toward the end.${isHighlighted ? '</span>' : ''}`;
    }

    // Add appreciation section before thank you
    if (toggles.appreciation) {
      const isHighlighted = highlightedId === 'appreciation';
      content += `

${isHighlighted ? '<span class="highlighted-text">' : ''}I know your time is valuable, and I'm genuinely grateful that you're taking a moment to support my growth.${isHighlighted ? '</span>' : ''}`;
    }

    // Add final part
    content += `

I truly appreciate you taking the time to do this. Your feedback will be key in this journey of growth. If I can ever return the favor, don't hesitate to reach out.

Please feel free to message me or contact voice@insightalks.com if you have any questions.
Thank you again!`;

    // Add "The Result" section if enabled
    if (toggles.result) {
      const isHighlighted = highlightedId === 'result';
      content += `

${isHighlighted ? '<span class="highlighted-text">' : ''}Once the process is complete, I'll receive a report with the results.
Everything will be aggregated and anonymous, allowing me to focus on the themes and patterns across feedback.${isHighlighted ? '</span>' : ''}`;
    }

    return content;
  };

  const handleCopy = async () => {
    const content = generateEmailContent();
    
    try {
      await navigator.clipboard.writeText(content);
      setCopied(true);
      toast.success('Email content copied to clipboard');
      
      // Reset copied state after 2 seconds
      setTimeout(() => {
        setCopied(false);
      }, 2000);
    } catch (err) {
      console.error('Failed to copy text: ', err);
      toast.error('Failed to copy to clipboard');
    }
  };

  if (!isOpen || !evaluator) return null;

  return (
    <div className="fixed inset-0 z-50 overflow-y-auto">
      <div className="flex min-h-screen items-center justify-center p-4">
        <motion.div
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          exit={{ opacity: 0 }}
          className="fixed inset-0 bg-black bg-opacity-50"
          onClick={closeModal}
        />
        
        <motion.div
          initial={{ scale: 0.95, opacity: 0 }}
          animate={{ scale: 1, opacity: 1 }}
          exit={{ scale: 0.95, opacity: 0 }}
          className="relative bg-white rounded-xl shadow-xl max-w-4xl w-full mx-4 max-h-[90vh] overflow-hidden flex flex-col"
        >
          {/* Header */}
          <div className="sticky top-0 bg-white z-10 px-6 py-4 border-b border-gray-200 flex justify-between items-center">
            <div className="flex items-center space-x-3">
              <Mail className="w-5 h-5 text-primary" />
              <h2 className="text-xl font-bold text-gray-900">Personalized Email for {evaluator.firstName} {evaluator.lastName}</h2>
            </div>
            <button
              onClick={closeModal}
              className="text-gray-400 hover:text-gray-500 transition-colors"
            >
              <X className="w-6 h-6" />
            </button>
          </div>

          <div className="flex flex-col md:flex-row flex-1 overflow-hidden">
            {/* Toggle Options */}
            <div className="w-full md:w-64 bg-gray-50 p-4 overflow-y-auto border-b md:border-b-0 md:border-r border-gray-200">
              <h3 className="font-medium text-gray-900 mb-3">Customize Message</h3>
              <div className="space-y-3">
                {toggleOptions.map((option) => (
                  <div key={option.id} className="flex items-center">
                    <label className="flex items-center cursor-pointer">
                      <div className="relative">
                        <input
                          type="checkbox"
                          className="sr-only"
                          checked={toggles[option.id]}
                          onChange={() => handleToggle(option.id)}
                        />
                        <div className={`block w-10 h-6 rounded-full transition-colors ${
                          toggles[option.id] ? 'bg-primary' : 'bg-gray-300'
                        }`}></div>
                        <div className={`absolute left-1 top-1 bg-white w-4 h-4 rounded-full transition-transform ${
                          toggles[option.id] ? 'transform translate-x-4' : ''
                        }`}></div>
                      </div>
                      <span className="ml-3 text-sm">{option.label}</span>
                    </label>
                  </div>
                ))}
              </div>
            </div>

            {/* Email Preview */}
            <div className="flex-1 p-6 overflow-y-auto">
              <style>{highlightAnimation}</style>
              <div className="bg-white border border-gray-200 rounded-lg p-6 shadow-sm mb-4">
                <div 
                  className="whitespace-pre-wrap font-sans text-gray-800 leading-relaxed"
                  dangerouslySetInnerHTML={{ __html: generateEmailContent() }}
                />
              </div>
              
              <div className="flex justify-end">
                <button
                  onClick={handleCopy}
                  className="flex items-center space-x-2 bg-primary text-dark px-6 py-3 rounded-lg hover:bg-primary-hover transition-colors"
                >
                  {copied ? (
                    <>
                      <Check className="w-5 h-5" />
                      <span>Copied!</span>
                    </>
                  ) : (
                    <>
                      <Copy className="w-5 h-5" />
                      <span>Copy to Clipboard</span>
                    </>
                  )}
                </button>
              </div>
            </div>
          </div>
        </motion.div>
      </div>
    </div>
  );
}